import React, { useState, useEffect } from 'react';
import useLang from '../hooks/useLang';

const CookieBanner = () => {
  const { lang } = useLang();
  const [isVisible, setIsVisible] = useState(false);
  const [showDetails, setShowDetails] = useState(false);

  const content = {
    de: {
      title: "Cookie-Einstellungen",
      description: "Wir verwenden Cookies, um Ihnen die bestmögliche Nutzererfahrung zu bieten. Technisch notwendige Cookies sind für die Funktion der Website erforderlich.",
      essential: "Technisch notwendige Cookies",
      essentialDesc: "Diese Cookies sind für die Grundfunktionen der Website erforderlich.",
      analytics: "Analyse-Cookies",
      analyticsDesc: "Helfen uns zu verstehen, wie Besucher mit der Website interagieren.",
      acceptAll: "Alle akzeptieren",
      acceptEssential: "Nur notwendige",
      settings: "Einstellungen",
      save: "Speichern",
      moreInfo: "Mehr Informationen in unserer",
      privacyPolicy: "Datenschutzerklärung"
    },
    es: {
      title: "Configuración de Cookies",
      description: "Utilizamos cookies para ofrecerle la mejor experiencia de usuario posible. Las cookies técnicamente necesarias son requeridas para el funcionamiento del sitio web.",
      essential: "Cookies técnicamente necesarias",
      essentialDesc: "Estas cookies son necesarias para las funciones básicas del sitio web.",
      analytics: "Cookies de análisis",
      analyticsDesc: "Nos ayudan a entender cómo los visitantes interactúan con el sitio web.",
      acceptAll: "Aceptar todo",
      acceptEssential: "Solo necesarias",
      settings: "Configuración",
      save: "Guardar",
      moreInfo: "Más información en nuestra",
      privacyPolicy: "Declaración de Privacidad"
    },
    it: {
      title: "Impostazioni Cookie",
      description: "Utilizziamo i cookie per offrirti la migliore esperienza utente possibile. I cookie tecnicamente necessari sono richiesti per il funzionamento del sito web.",
      essential: "Cookie tecnicamente necessari",
      essentialDesc: "Questi cookie sono necessari per le funzioni di base del sito web.",
      analytics: "Cookie di analisi",
      analyticsDesc: "Ci aiutano a capire come i visitatori interagiscono con il sito web.",
      acceptAll: "Accetta tutto",
      acceptEssential: "Solo necessari",
      settings: "Impostazioni",
      save: "Salva",
      moreInfo: "Maggiori informazioni nella nostra",
      privacyPolicy: "Dichiarazione sulla Privacy"
    }
  };

  const t = content[lang] || content.de;

  useEffect(() => {
    // Check if user has already made a choice
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      setIsVisible(true);
    }
  }, []);

  const handleAcceptAll = () => {
    localStorage.setItem('cookieConsent', 'all');
    localStorage.setItem('analyticsEnabled', 'true');
    setIsVisible(false);
    // Here you would initialize analytics scripts
    console.log('Analytics enabled');
  };

  const handleAcceptEssential = () => {
    localStorage.setItem('cookieConsent', 'essential');
    localStorage.setItem('analyticsEnabled', 'false');
    setIsVisible(false);
    console.log('Only essential cookies enabled');
  };

  const handleSaveSettings = () => {
    const analyticsCheckbox = document.getElementById('analytics-cookies');
    const analyticsEnabled = analyticsCheckbox.checked;
    
    localStorage.setItem('cookieConsent', 'custom');
    localStorage.setItem('analyticsEnabled', analyticsEnabled.toString());
    setIsVisible(false);
    
    if (analyticsEnabled) {
      console.log('Analytics enabled');
    } else {
      console.log('Analytics disabled');
    }
  };

  if (!isVisible) return null;

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg z-50">
      <div className="container mx-auto px-4 py-4">
        {!showDetails ? (
          // Simple banner
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="flex-1">
              <h3 className="font-semibold text-gray-900 mb-2">{t.title}</h3>
              <p className="text-sm text-gray-600 mb-2">
                {t.description}
              </p>
              <p className="text-xs text-gray-500">
                {t.moreInfo} <button className="text-blue-600 hover:text-blue-800 underline">{t.privacyPolicy}</button>
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 min-w-fit">
              <button
                onClick={() => setShowDetails(true)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t.settings}
              </button>
              <button
                onClick={handleAcceptEssential}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t.acceptEssential}
              </button>
              <button
                onClick={handleAcceptAll}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t.acceptAll}
              </button>
            </div>
          </div>
        ) : (
          // Detailed settings
          <div className="max-w-2xl mx-auto">
            <h3 className="font-semibold text-gray-900 mb-4">{t.title}</h3>
            
            <div className="space-y-4 mb-6">
              {/* Essential Cookies */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{t.essential}</h4>
                  <span className="text-sm text-gray-500 bg-gray-100 px-2 py-1 rounded">
                    Immer aktiv
                  </span>
                </div>
                <p className="text-sm text-gray-600">{t.essentialDesc}</p>
              </div>

              {/* Analytics Cookies */}
              <div className="border border-gray-200 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-gray-900">{t.analytics}</h4>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      id="analytics-cookies"
                      className="sr-only peer"
                      defaultChecked={false}
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
                  </label>
                </div>
                <p className="text-sm text-gray-600">{t.analyticsDesc}</p>
              </div>
            </div>

            <div className="flex flex-col sm:flex-row gap-2 justify-end">
              <button
                onClick={() => setShowDetails(false)}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Zurück
              </button>
              <button
                onClick={handleAcceptEssential}
                className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                {t.acceptEssential}
              </button>
              <button
                onClick={handleSaveSettings}
                className="px-4 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                {t.save}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CookieBanner;